import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  Grid,
  FormControl,
  FormHelperText,
  TypographyProps,
  ButtonProps,
  useTheme,
  useMediaQuery,
  Dialog,
  BoxProps,
} from '@mui/material';
import PhoneInput from 'react-phone-number-input';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { useLocation } from 'react-router-dom';
import { Countries } from '../../../../constants';
import { getAuthUserFromLocalStorage } from '../../../../helper';
import { setAuthUser } from '../../../../store/AuthSlice';
import { getUsageByUserId } from '../../../../store/AccessLimitSlice/AccessLimitApi';
import { useAppSelector } from '../../../../hooks';
import {
  getAccessLimitState,
  toggleLeadGenModal,
} from '../../../../store/AccessLimitSlice';
import { createAnalytics } from '../../../../store/AnalyticsSlice';
import { isVideoContainer } from '..';

const buttonProps: ButtonProps = {
  style: {
    fontWeight: 600,
    padding: '10px',
    color: 'white',
  },
  variant: 'contained',
};

const roleButtonProps: ButtonProps = {
  sx: {
    flex: 1,
    boxShadow: 'unset',
    backgroundColor: '#E2E8F0',
    color: 'black',
    '&:hover': {
      backgroundColor: '#1E293B',
      color: '#E2E8F0',
    },
  },
  variant: 'contained',
};

const label: TypographyProps = {
  sx: {
    mb: '2px',
    fontSize: '12px',
  },
};

const leftSide: BoxProps['sx'] = {
  background: `url(${process.env.PUBLIC_URL}/images/leadGenModal/background.png), #F1F5F9`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  borderRadius: '15px',
  padding: '25px',
  position: 'relative',
  margin: 'unset',
  width: '100%',
  flex: 1,
  borderTopRightRadius: { xs: '15', sm: 'unset' },
  borderBottomRightRadius: { xs: 'unset', sm: 'unset' },
  borderBottomLeftRadius: { xs: 'unset', sm: 'unset' },
};

const rightSide: BoxProps['sx'] = {
  borderRadius: '15px',
  padding: { xs: '15px', sm: '25px' },
  position: 'relative',
  flex: 1,
};

// DATA
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  ? Intl.DateTimeFormat().resolvedOptions().timeZone
  : 'Asia/Singapore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let defaultCountryCode: any = 'SG';

const defaultUserTimezone = Countries.find(
  (country) => country.value === timezone
);

if (defaultUserTimezone && defaultUserTimezone.countryCode) {
  defaultCountryCode = defaultUserTimezone.countryCode;
}

export interface IResourceLeadGenState {
  fullName: string;
  phoneNumber: string;
  country: string;
}

const initialState: IResourceLeadGenState = {
  fullName: '',
  phoneNumber: '',
  country: timezone,
};

interface LeadGenModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (values: IResourceLeadGenState) => void;
}

const defaultErrorData = {
  visible: false,
  message: '',
};

const LeadGenModalForResources: React.FC<LeadGenModalProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const location = useLocation();

  // State
  const [state, setState] = useState(initialState);
  const [role, setRole] = useState<string | null>(null);
  const [errors, setErrors] = useState({
    fullName: { ...defaultErrorData },
    phoneNumber: { ...defaultErrorData },
  });

  // Store
  const accessLimitState = useAppSelector(getAccessLimitState);
  const authUser = getAuthUserFromLocalStorage();
  const { isLeadGenSuccess, status } = accessLimitState;

  useEffect(() => {
    if (isLeadGenSuccess) {
      if (authUser) {
        dispatch(setAuthUser(authUser));
        dispatch(getUsageByUserId(authUser._id));
      }
      dispatch(toggleLeadGenModal());
    }
  }, [isLeadGenSuccess]);

  useEffect(() => {
    if (state.fullName) {
      let _nameError = { ...defaultErrorData };

      if (state.fullName.length < 3) {
        _nameError = {
          visible: true,
          message: 'Name should have at least 3 characters',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        fullName: _nameError,
      }));
    }
  }, [state.fullName]);

  useEffect(() => {
    if (state.phoneNumber) {
      let _nameError = { ...defaultErrorData };

      if (state.phoneNumber.length < 9) {
        _nameError = {
          visible: true,
          message: 'Phone Number should have at least 9 numbers',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        phoneNumber: _nameError,
      }));
    }
  }, [state.phoneNumber]);

  const handleButtonClick = () => {
    dispatch(
      createAnalytics({
        type: 'LP - Leadgen Form',
        data: {
          selectedRole: role,
          fullName: state.fullName,
          phoneNumber: state.phoneNumber,
          country: state.country,
          source: isVideoContainer() ? 'Videos' : 'Content',
          url: location.pathname.replace('/learning-portal/dashboard/', ''),
        },
        userId: authUser?._id,
      })
    );
    onConfirm(state);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;
    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const submitButtonDisabled =
    state.phoneNumber.trim().length < 9 || state.fullName.trim().length < 3;

  const renderForm = () => (
    <Grid container>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <Typography {...label}>Full Name *</Typography>
          <TextField
            size="small"
            name="fullName"
            type="text"
            placeholder="Type your full name"
            onChange={handleInputChange}
            error={errors.fullName.visible}
            helperText={errors.fullName.message || ' '}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <Typography {...label}>Phone Number *</Typography>
        <PhoneInput
          international
          withCountryCallingCode
          defaultCountry={defaultCountryCode}
          placeholder="Enter phone number"
          value={state.phoneNumber}
          onChange={(value) => {
            if (value) {
              setState((prevState) => ({
                ...prevState,
                phoneNumber: value,
              }));
            }
          }}
          className="phone-number-input-custom-styles"
        />
        <FormHelperText style={{ marginLeft: '14px', color: '#FF2B2D' }}>
          {errors.phoneNumber.message || ' '}
        </FormHelperText>
      </Grid>
    </Grid>
  );

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      hideBackdrop
      onClose={onClose}
      PaperProps={{
        sx: {
          maxWidth: { xs: '70%', sm: '700px' },
          position: 'relative',
          borderRadius: '15px',
        },
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="space-between"
        direction={belowMd ? 'column' : 'row'}
        sx={{ width: '100%' }}
      >
        <Box sx={leftSide} borderRight={belowMd ? 'unset' : '1px solid #ddd'}>
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/images/leadGenModal/trophy.png`}
            alt="Trophy"
            sx={{
              height: belowMd ? '100px' : '215px',
              width: belowMd ? '100px' : '215px',
              marginLeft: belowMd ? 'unset' : '-30px',
              mt: belowMd ? 'unset' : '20%',
            }}
          />
          <Typography
            color="textSecondary"
            fontSize={belowMd ? '14px' : '16px'}
          >
            Lets fill small detail to
          </Typography>
          <Typography
            fontSize={belowMd ? '17px' : '27px'}
            fontWeight="bold"
            marginBottom={belowMd ? '1%' : '5%'}
          >
            Complete Your Learning Quest!
          </Typography>
        </Box>
        <Box sx={rightSide}>
          {role === null ? (
            <>
              <Typography
                variant="h6"
                mb={3}
                textAlign="center"
                fontWeight="bold"
              >
                I am here as a
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}/images/leadGenModal/right-design.png`}
                  alt="Illustration"
                  sx={{
                    width: '80%',
                    height: 'auto',
                  }}
                />
              </Box>
              <Stack
                direction="row"
                justifyContent="center"
                spacing={belowMd ? 1 : 3}
              >
                <Button {...roleButtonProps} onClick={() => setRole('Tutor')}>
                  Tutor
                </Button>
                <Button {...roleButtonProps} onClick={() => setRole('Student')}>
                  Student
                </Button>
              </Stack>
            </>
          ) : (
            <>
              <Typography
                fontSize={belowMd ? '16px' : '20px'}
                textAlign="center"
                mb={belowMd ? 2 : 3}
                fontWeight="bold"
              >
                Tell us about your self
              </Typography>
              {renderForm()}
              <LoadingButton
                {...buttonProps}
                sx={{
                  mt: belowMd ? 1 : 2,
                  backgroundColor: submitButtonDisabled ? '#E2E8F0' : '#1E293B',
                  width: '100%',
                }}
                disabled={submitButtonDisabled}
                onClick={handleButtonClick}
                loading={status === 'loading'}
              >
                Continue Learning
              </LoadingButton>
            </>
          )}
        </Box>
      </Stack>
    </Dialog>
  );
};

export default LeadGenModalForResources;
